// Projection through the perspective camera: world to canvas, and canvas rays back to the plane.

import { PerspectiveCamera, Plane, Raycaster, Vector2, Vector3 } from "three";

import type { PointXY, PointXYZ } from "@lpviz/math/types";
import { perspectiveSnapshotAtDistance } from "./perspective";
import { getViewportSize, snapPoint, type ViewportPerspectivePose, type ViewportRect, type ViewportRenderSnapshot } from "./snapshot";

/** How a canvas point reads as a world point while the editor is driven in 3D. */
export type Viewport3DPointerOptions = {
  zScale: number;
  snapToGrid: boolean;
  /** an editor gesture is in progress, so the point is clamped to a sane span around the target */
  interacting: boolean;
  /** the gesture's anchor, whose view-aligned plane catches rays that miss the floor */
  viewAnchor3D?: PointXYZ | undefined;
};

const MAX_3D_DRAG_BOUND = 5000;
const VIEW_DRAG_BOUND_MULTIPLIER = 6;
const MAX_3D_PLANE_SLOPE = 2;
// Ray-plane denominator threshold: if |ray · normal| < this, the ray is nearly
// parallel to the plane and the intersection point is too far away to be useful.
const PLANE_PARALLEL_THRESHOLD = 0.08;
const Z_PLANE_NORMAL: PointXYZ = { x: 0, y: 0, z: 1 };
// Far-offscreen sentinel for points that have no on-screen position; finite
// so distance-based hit tests against it are well-defined and never match.
const OFFSCREEN_CANVAS_COORD = -1e9;

const projectionCamera = new PerspectiveCamera();
const projectionTarget = new Vector3();
const projectionRaycaster = new Raycaster();
const projectionPointerNdc = new Vector2();
const projectionPointerWorld = new Vector3();
const projectionPlaneNormal = new Vector3();
const projectionPlanePoint = new Vector3();
const projectionPlane = new Plane();
const projectedPosition = new Vector3();
const projectionViewDir = new Vector3();
const projectionZoomAxis = new Vector3();

// Poses a camera from the snapshot: by default the package's one scratch camera, which every
// reader configures first and nothing relies on across calls; a caller that owns a camera passes
// it and the Vector3 that should hold the target.
export function configurePerspectiveCameraFromSnapshot(snapshot: ViewportRenderSnapshot, camera = projectionCamera, target = projectionTarget): PerspectiveCamera {
  camera.fov = snapshot.perspective.fov;
  camera.aspect = snapshot.perspective.aspect;
  camera.near = snapshot.perspective.near;
  camera.far = snapshot.perspective.far;
  camera.position.set(snapshot.perspective.position.x, snapshot.perspective.position.y, snapshot.perspective.position.z);
  camera.up.set(snapshot.perspective.up.x, snapshot.perspective.up.y, snapshot.perspective.up.z);
  camera.lookAt(target.set(snapshot.target.x, snapshot.target.y, snapshot.target.z));
  camera.updateMatrixWorld();
  camera.updateProjectionMatrix();
  return camera;
}

function clamp3DInteractionPoint(point: PointXY, snapshot: ViewportRenderSnapshot, rect: ViewportRect, options: Viewport3DPointerOptions): PointXY {
  if (!options.interacting) {
    return point;
  }

  const { width, height } = getViewportSize(snapshot, rect);
  const viewSpan = Math.max(width, height) * snapshot.unitsPerPixel;
  const viewBound = Math.max(60, viewSpan * VIEW_DRAG_BOUND_MULTIPLIER);
  const slopeScaler = Math.max(options.zScale, 0.001);
  const slopeBound = (MAX_3D_PLANE_SLOPE * 100) / slopeScaler;
  const bound = Math.min(MAX_3D_DRAG_BOUND, Math.min(viewBound, slopeBound));
  if (!Number.isFinite(bound) || bound <= 0) {
    return point;
  }

  return {
    x: Math.max(snapshot.target.x - bound, Math.min(snapshot.target.x + bound, point.x)),
    y: Math.max(snapshot.target.y - bound, Math.min(snapshot.target.y + bound, point.y)),
  };
}

export function projectWorldPosition3D(snapshot: ViewportRenderSnapshot, rect: ViewportRect, position: PointXYZ): PointXY {
  configurePerspectiveCameraFromSnapshot(snapshot);
  const { width, height } = getViewportSize(snapshot, rect);
  projectedPosition.set(position.x, position.y, position.z).applyMatrix4(projectionCamera.matrixWorldInverse);
  if (projectedPosition.z >= 0) {
    // Behind the camera: projecting would mirror the point onto the screen,
    // making it spuriously hoverable/draggable.
    return { x: OFFSCREEN_CANVAS_COORD, y: OFFSCREEN_CANVAS_COORD };
  }
  projectedPosition.applyMatrix4(projectionCamera.projectionMatrix);

  return {
    x: ((projectedPosition.x + 1) / 2) * width,
    y: ((1 - projectedPosition.y) / 2) * height,
  };
}

export function toCanvasCoords3D(snapshot: ViewportRenderSnapshot, rect: ViewportRect, point: PointXY, z: number | undefined, zScale: number): PointXY {
  // Render layers flatten z by transitionZMultiplier during the 2D/3D
  // transition; match them so screen positions agree with drawn geometry.
  return projectWorldPosition3D(snapshot, rect, {
    x: point.x,
    y: point.y,
    z: (((z ?? 0) * zScale) / 100) * snapshot.transitionZMultiplier,
  });
}

// Where the ray through canvas-space `point` meets the plane, on the package's scratch vector
// (valid until the next call), or null when the ray is near-parallel to it (the hit would be
// billions of units away: test the denominator ray · normal first) or misses it.
function castCanvasRayOntoPlane(snapshot: ViewportRenderSnapshot, rect: ViewportRect, point: PointXY, normal: Vector3, coplanarPoint: PointXYZ): Vector3 | null {
  const { width, height } = getViewportSize(snapshot, rect);
  projectionRaycaster.setFromCamera(projectionPointerNdc.set((point.x / width) * 2 - 1, -((point.y / height) * 2 - 1)), configurePerspectiveCameraFromSnapshot(snapshot));
  projectionPlane.setFromNormalAndCoplanarPoint(normal, projectionPlanePoint.set(coplanarPoint.x, coplanarPoint.y, coplanarPoint.z));
  if (Math.abs(projectionRaycaster.ray.direction.dot(projectionPlane.normal)) < PLANE_PARALLEL_THRESHOLD) return null;
  return projectionRaycaster.ray.intersectPlane(projectionPlane, projectionPointerWorld);
}

// the plane's hit as a plane point, or null when there is none or it is not finite
function intersectCanvasRayWithPlane(snapshot: ViewportRenderSnapshot, rect: ViewportRect, point: PointXY, normal: PointXYZ, coplanarPoint: PointXYZ): PointXY | null {
  const hit = castCanvasRayOntoPlane(snapshot, rect, point, projectionPlaneNormal.set(normal.x, normal.y, normal.z), coplanarPoint);
  return hit && Number.isFinite(hit.x) && Number.isFinite(hit.y) ? { x: hit.x, y: hit.y } : null;
}

// the view direction, which is the normal of every view-aligned plane
const viewDirection = (snapshot: ViewportRenderSnapshot): Vector3 =>
  projectionViewDir.set(snapshot.target.x - snapshot.perspective.position.x, snapshot.target.y - snapshot.perspective.position.y, snapshot.target.z - snapshot.perspective.position.z).normalize();

/**
 * The point on the view-aligned plane through `through` under the canvas `point`, or null. The
 * plane faces the camera, so the ray cannot be parallel to it for reasonable FOVs.
 */
export function viewAlignedPlaneHit(snapshot: ViewportRenderSnapshot, rect: ViewportRect, point: PointXY, through: PointXYZ): PointXYZ | null {
  const hit = castCanvasRayOntoPlane(snapshot, rect, point, viewDirection(snapshot), through);
  return hit ? { x: hit.x, y: hit.y, z: hit.z } : null;
}

export function projectCanvasPointToWorldPlane(snapshot: ViewportRenderSnapshot, rect: ViewportRect, point: PointXY, z = 0): PointXY | null {
  return intersectCanvasRayWithPlane(snapshot, rect, point, Z_PLANE_NORMAL, { x: 0, y: 0, z });
}

export function toLogicalCoords3D(snapshot: ViewportRenderSnapshot, rect: ViewportRect, point: PointXY, options: Viewport3DPointerOptions): PointXY {
  let hit = projectCanvasPointToWorldPlane(snapshot, rect, point);
  if (!hit && options.viewAnchor3D) {
    // a ray that misses the floor meets the view-aligned plane through the drag anchor instead
    const anchored = viewAlignedPlaneHit(snapshot, rect, point, options.viewAnchor3D);
    hit = anchored && Number.isFinite(anchored.x) && Number.isFinite(anchored.y) ? { x: anchored.x, y: anchored.y } : null;
  }
  return snapPoint(clamp3DInteractionPoint(hit ?? { x: snapshot.target.x, y: snapshot.target.y }, snapshot, rect, options), options.snapToGrid);
}

/**
 * The pose after a wheel zoom to `distance` from its target that keeps the world point under the
 * canvas `point` still: that point is read off the view-aligned plane through the target before
 * and after the camera moves along its axis, and camera and target both shift by the difference.
 * With nothing under the cursor, the zoom is toward the target.
 */
export function zoomPerspectivePoseAtCanvasPoint(base: ViewportRenderSnapshot, pose: ViewportPerspectivePose, rect: ViewportRect, point: PointXY, distance: number): ViewportPerspectivePose {
  const axis = projectionZoomAxis.set(pose.position.x - pose.target.x, pose.position.y - pose.target.y, pose.position.z - pose.target.z);
  const length = axis.length();
  if (!(length > 0)) return pose;
  axis.normalize();
  const moved: ViewportPerspectivePose = { ...pose, position: { x: pose.target.x + axis.x * distance, y: pose.target.y + axis.y * distance, z: pose.target.z + axis.z * distance } };
  const before = viewAlignedPlaneHit(perspectiveSnapshotAtDistance(base, pose, length, rect), rect, point, pose.target);
  if (!before) return moved;
  const after = viewAlignedPlaneHit(perspectiveSnapshotAtDistance(base, moved, distance, rect), rect, point, pose.target);
  if (!after) return moved;
  const delta = { x: before.x - after.x, y: before.y - after.y, z: before.z - after.z };
  return {
    position: { x: moved.position.x + delta.x, y: moved.position.y + delta.y, z: moved.position.z + delta.z },
    up: pose.up,
    target: { x: pose.target.x + delta.x, y: pose.target.y + delta.y, z: pose.target.z + delta.z },
  };
}
